import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from 'use-toast-mui';
import dayjs from 'dayjs';
import CommonDatePicker from '../../../../form-components/CommonDatePicker';
import { updateExpireDateExtend } from '../../../../api/user';
import { getExpireDateActiveMember } from '../../../../api/expiration';
import { errorMessages } from '../../../../utils/methods';

import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormRow,
  FormLabel,
  FormValue,
  StyledInput,
  InputSuffix,
  CheckboxGroup,
  CheckboxItem,
  StyledCheckbox,
  CheckboxLabel,
  CountText,
  MemoTextarea,
  InfoSection,
  InfoList,
  InfoItem,
  ModalFooter,
  CancelButton,
  SubmitButton,
  InfoHighText,
  ErrorFieldWrapper,
  ErrorCheckboxWrapper,
} from './ExpirationExtensionModalStyled';

const ExpirationExtensionModal = ({ open, onClose, memberCount = 0, memberUsers, onSubmit }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  // console.log('memberUsers : ' + JSON.stringify(memberUsers, null, 2));

  // State
  const [baseDate, setBaseDate] = useState(new Date());
  const [extensionDays, setExtensionDays] = useState('');
  const [extensionItems, setExtensionItems] = useState({
    membership: false,
    periodProduct: false,
    locker: false,
  });
  const [ignoreSuspendPeriod, setIgnoreSuspendPeriod] = useState(false);
  const [memo, setMemo] = useState('');
  const [errorFields, setErrorFields] = useState({});

  // 필드 값 변경 시 해당 에러 제거
  useEffect(() => {
    setErrorFields(prev => {
      const updated = { ...prev };
      if (extensionDays) delete updated.extensionDays;
      if (extensionItems.membership || extensionItems.periodProduct /* || extensionItems.locker */) {
        delete updated.extensionItems;
      }
      if (memo?.trim()) delete updated.memo;
      return updated;
    });
  }, [extensionDays, extensionItems, memo]);

  // 날짜 형식 변환 (YYYY-MM-DD)
  const formattedDate = dayjs(baseDate).format('YYYY-MM-DD');

  // 기준 날짜로 유효 회원 조회
  const { data: activeMemberData } = useQuery({
    queryKey: ['expireDateActiveMembers', user?.branch?.branchId, formattedDate],
    queryFn: () => getExpireDateActiveMember(user?.branch?.branchId, formattedDate, user?.token),
    enabled: open && !!user?.branch?.branchId && !!user?.token,
  });

  // 연장 회원 수 (API 응답 또는 기본값)
  const activeMemberCount = activeMemberData?.length || activeMemberData?.memberIds?.length || 0;

  // Mutation
  const extendMutation = useMutation({
    mutationFn: data => updateExpireDateExtend(data),
    onSuccess: () => {
      toast.success(t('만료일이 연장되었습니다'));
      onSubmit?.();
      handleClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // Handlers
  const handleExtensionItemChange = key => {
    setExtensionItems(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = () => {
    const errors = {};

    // 유효성 검사
    if (!extensionDays || Number(extensionDays) <= 0) {
      errors.extensionDays = true;
    }

    // 연장 항목 체크 여부 확인
    const hasExtensionType = extensionItems.membership || extensionItems.periodProduct /* || extensionItems.locker */;
    if (!hasExtensionType) {
      errors.extensionItems = true;
    }

    if (!memo?.trim()) {
      errors.memo = true;
    }

    // 에러가 있으면 설정하고 토스트 메시지 표시
    if (Object.keys(errors).length > 0) {
      setErrorFields(errors);
      const firstError = Object.keys(errors)[0];
      const errorMessages = {
        extensionDays: '연장일을 입력해주세요',
        extensionItems: '연장 항목을 선택해주세요',
        memo: '메모를 입력해주세요',
      };
      toast.error(t(errorMessages[firstError]));
      return;
    }

    // 회원 ID 배열 확인
    const memberIds = activeMemberData || [];
    if (memberIds.length === 0) {
      toast.error(t('연장할 회원이 없습니다'));
      return;
    }

    // extensionTypes 배열 생성
    const extensionTypes = [];
    if (extensionItems.membership) extensionTypes.push('TICKET');
    if (extensionItems.periodProduct) extensionTypes.push('PASS');
    // if (extensionItems.locker) extensionTypes.push('LOCKER');

    const requestData = {
      branchId: user?.branch?.branchId,
      memberIds: memberIds,
      extensionDays: Number(extensionDays),
      extensionTypes: extensionTypes,
      includeStoppedPeriod: ignoreSuspendPeriod,
      reason: memo || '',
    };

    extendMutation.mutate(requestData);
  };

  const handleClose = () => {
    // Reset state
    setBaseDate(new Date());
    setExtensionDays('');
    setErrorFields({});
    setExtensionItems({
      membership: false,
      periodProduct: false,
      locker: false,
    });
    setIgnoreSuspendPeriod(false);
    setMemo('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalOverlay onClick={handleClose}>
        <ModalContainer onClick={e => e.stopPropagation()}>
          {/* Header */}
          <ModalHeader>
            <ModalTitle>{t('만료일 연장')}</ModalTitle>
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>

          {/* Content */}
          <ModalContent>
            {/* 기준 날짜 */}
            <FormRow>
              <FormLabel>{t('기준 날짜')}</FormLabel>
              <FormValue>
                <CommonDatePicker
                  value={baseDate}
                  onChange={newValue => setBaseDate(newValue)}
                  sx={{ width: '160px' }}
                />
              </FormValue>
            </FormRow>

            {/* 연장일 */}
            <FormRow>
              <FormLabel>{t('연장일')}</FormLabel>
              <FormValue>
                <ErrorFieldWrapper hasError={errorFields.extensionDays}>
                  <StyledInput
                    type="number"
                    value={extensionDays}
                    onChange={e => setExtensionDays(e.target.value)}
                    placeholder=""
                  />
                </ErrorFieldWrapper>
                <InputSuffix>{t('일')}</InputSuffix>
              </FormValue>
            </FormRow>

            {/* 연장 항목 */}
            <FormRow>
              <FormLabel>{t('연장 항목')}</FormLabel>
              <FormValue>
                <ErrorCheckboxWrapper hasError={errorFields.extensionItems}>
                  <CheckboxGroup>
                    <CheckboxItem>
                      <StyledCheckbox
                        checked={extensionItems.membership}
                        onChange={() => handleExtensionItemChange('membership')}
                      />
                      <CheckboxLabel>{t('수강권')}</CheckboxLabel>
                    </CheckboxItem>
                    <CheckboxItem>
                      <StyledCheckbox
                        checked={extensionItems.periodProduct}
                        onChange={() => handleExtensionItemChange('periodProduct')}
                      />
                      <CheckboxLabel>{t('이용권')}</CheckboxLabel>
                    </CheckboxItem>
                    {/* <CheckboxItem>
                      <StyledCheckbox
                        checked={extensionItems.locker}
                        onChange={() => handleExtensionItemChange('locker')}
                      />
                      <CheckboxLabel>{t('락커')}</CheckboxLabel>
                    </CheckboxItem> */}
                  </CheckboxGroup>
                </ErrorCheckboxWrapper>
              </FormValue>
            </FormRow>

            {/* 연장 회원 */}
            <FormRow>
              <FormLabel>{t('연장 회원')}</FormLabel>
              <FormValue>
                <CountText>
                  {activeMemberCount}
                  {t('명')}
                </CountText>
              </FormValue>
            </FormRow>

            {/* 중지 기간 */}
            <FormRow>
              <FormLabel>{t('중지 기간')}</FormLabel>
              <FormValue>
                <CheckboxItem>
                  <StyledCheckbox
                    checked={ignoreSuspendPeriod}
                    onChange={() => setIgnoreSuspendPeriod(!ignoreSuspendPeriod)}
                  />
                  <CheckboxLabel>{t('중지된 기간 관계 없이 일괄 연장')}</CheckboxLabel>
                </CheckboxItem>
              </FormValue>
            </FormRow>

            {/* 메모 */}
            <FormRow style={{ alignItems: 'flex-start' }}>
              <FormLabel style={{ paddingTop: '10px' }}>{t('메모')}</FormLabel>
              <FormValue>
                <ErrorFieldWrapper hasError={errorFields.memo}>
                  <MemoTextarea
                    multiline
                    rows={1}
                    value={memo}
                    onChange={e => setMemo(e.target.value)}
                    placeholder={t('메모를 입력해 주세요')}
                  />
                </ErrorFieldWrapper>
              </FormValue>
            </FormRow>
            {/* Info Section */}
            <InfoSection>
              <InfoList>
                <InfoItem>
                  <InfoHighText className="highlight">{t('모든 유효 회원들')}</InfoHighText>
                  {t('의 만료일을 연장할 수 있습니다.')}
                </InfoItem>
                <InfoItem>{t('항목에는 수강권, 이용권으로 선택할 수 있습니다.')}</InfoItem>
                <InfoItem>
                  {t('기준 날짜 이후에 사용 예정인 이용권은 기준 날짜~시작일 사이 기간은 제외됩니다.')}
                </InfoItem>
              </InfoList>
            </InfoSection>
          </ModalContent>

          {/* Footer */}
          <ModalFooter>
            <CancelButton onClick={handleClose} disabled={extendMutation.isPending}>
              {t('취소')}
            </CancelButton>
            <SubmitButton onClick={handleSubmit} disabled={extendMutation.isPending}>
              {extendMutation.isPending ? t('처리중...') : t('일괄연장')}
            </SubmitButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    </Modal>
  );
};

export default ExpirationExtensionModal;
